import {
    jwtAuthenticator,
    NatsConnection,
    wsconnect,
} from "@nats-io/nats-core";
import { fetchWorkspaceNatsJwt } from "@api/nats/nats.api";
import { NATS_SERVERS } from "contexts/nats.lib";
import { OpenedConnection, createConnectionPool } from "./connectionPool.lib";

const JWT_REFRESH_AFTER_MS = 45 * 60 * 1000; // tokens expire after 1 hour
const RETRY_DELAY_MS = 5_000;
const RELEASE_GRACE_MS = 10_000;

const watchForReplacement = (connection: NatsConnection): Promise<void> =>
    new Promise((resolve) => {
        const refreshTimer = setTimeout(resolve, JWT_REFRESH_AFTER_MS);
        const settle = () => {
            clearTimeout(refreshTimer);
            resolve();
        };

        void (async () => {
            for await (const status of connection.status()) {
                if (
                    status.type === "error" &&
                    status.error.name === "UserAuthenticationExpiredError"
                ) {
                    break;
                }
            }
            settle();
        })();
        void connection.closed().then(settle);
    });

const openWorkspaceConnection = async (
    workspaceId: string,
): Promise<OpenedConnection<NatsConnection>> => {
    const { jwt } = await fetchWorkspaceNatsJwt(workspaceId);
    const connection = await wsconnect({
        servers: NATS_SERVERS,
        authenticator: jwtAuthenticator(jwt),
    });

    return { connection, replaceWhen: watchForReplacement(connection) };
};

/** Connections authorized for a single workspace, shared by every presence hook that needs one. */
export const workspaceConnectionPool = createConnectionPool({
    open: openWorkspaceConnection,
    retryDelayMs: RETRY_DELAY_MS,
    releaseGraceMs: RELEASE_GRACE_MS,
});
